"use client";

import React, { useActionState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { signIn } from "./actions";
import CenteredContainer from "../../common/components/CenteredContainer";
import { SignInActionMessages } from "@/src/util/constants";
import { useRouter } from "next/navigation";
import { Alert } from "@mui/material";

export default function SignIn() {
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [alert, setAlert] = React.useState<React.JSX.Element>();

  const router = useRouter();

  const [message, formAction, isPending] = useActionState(signIn, undefined);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (emailError || passwordError) {
      event.preventDefault();
      return;
    }
  };

  const handleForgotPasswordLink = () => {
    const email = document.getElementById("email") as HTMLInputElement;
    router.push(`/forgotPassword?email=${email.value}`);
  };

  const validateInputs = () => {
    const email = document.getElementById("email") as HTMLInputElement;
    const password = document.getElementById("password") as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmail(email.value);
      setEmailError("");
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError("Password must be at least 6 characters long.");
      isValid = false;
    } else {
      setPasswordError("");
    }

    return isValid;
  };

  const sendActivationEmail = async () => {
    fetch(`/notifySignUp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: email }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok || !data.success) {
          console.error(`Failed to notify sign up of user ${email}`, data);
        }
      })
      .catch((error) => {
        console.error(`Failed to notify sign up of user ${email}`, error);
      });
  };

  useEffect(() => {
    if (!message) return;

    switch (message) {
      case SignInActionMessages.SUCCESS:
        router.push("/");
        break;
      case SignInActionMessages.USER_NOT_CONFIRMED:
        setAlert(
          <Alert severity="warning">
            This email address needs to be verified.
            <Link
              href={`/verifyAccount?email=${email}`}
              variant="body2"
              sx={{ ml: 1 }}
            >
              Verify account.
            </Link>
          </Alert>
        );

        router.push(`/verifyAccount?email=${email}`);
        break;
      case SignInActionMessages.USER_NOT_ACTIVATED:
        router.push("/pendingActivation");
        sendActivationEmail();
        break;
      case SignInActionMessages.USER_NOT_FOUND:
        setAlert(
          <Alert severity="warning">
            This email address is not associated to an account.
            <Link href="/signUp" variant="body2" sx={{ ml: 1 }}>
              Sign up.
            </Link>
          </Alert>
        );
        break;
      case SignInActionMessages.INVALID_PASSWORD:
        setAlert(
          <Alert severity="warning">
            The password you entered is incorrect.
            <Link href="/forgotPassword" variant="body2" sx={{ ml: 1 }}>
              Forgot your password?
            </Link>
          </Alert>
        );
        break;
      case SignInActionMessages.UNKNOWN_ERROR:
        setAlert(
          <Alert severity="error">
            {SignInActionMessages.UNKNOWN_ERROR} Please try again. If the issue
            persists, please email our support team at{" "}
            <Link href="mailto:help@bouncepoint.io">help@bouncepoint.io</Link>.
          </Alert>
        );
        break;
      default:
        break;
    }
  }, [message, email]);

  return (
    <CenteredContainer>
      <Typography
        component="h1"
        variant="h4"
        sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
      >
        Sign in
      </Typography>
      {alert && alert}
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        action={formAction}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: 2,
        }}
      >
        <FormControl>
          <FormLabel htmlFor="email">Email</FormLabel>
          <TextField
            error={!!emailError}
            helperText={emailError}
            id="email"
            type="email"
            name="email"
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={emailError ? "error" : "primary"}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="password">Password</FormLabel>
          <TextField
            error={!!passwordError}
            helperText={passwordError}
            name="password"
            placeholder="••••••"
            type="password"
            id="password"
            autoComplete="current-password"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={passwordError ? "error" : "primary"}
          />
        </FormControl>
        {/* <FormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label="Remember me"
        /> */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          onClick={validateInputs}
          loading={isPending}
        >
          Sign in
        </Button>
        <Link
          type="button"
          component="button"
          onClick={handleForgotPasswordLink}
          variant="body2"
          sx={{ alignSelf: "center" }}
        >
          Forgot your password?
        </Link>
      </Box>
    </CenteredContainer>
  );
}
